import type { DateRange } from 'react-day-picker'
import { format } from 'date-fns/format'
import type { UseFormSetValue } from 'react-hook-form'
import type { LOCATION_SEARCH_TYPE } from '@/types'
import type {
  FlightFormStateType,
  FLIGHT_TYPE,
  FlightRecordType
} from '../../types'
import { FLIGHT_TYPE_OPTIONS } from '../constants'
import { ROUND_TRIP } from '../../constants'

export const parseDate = (dateString: string | undefined) => {
  if (!dateString || dateString.length !== 8) return undefined

  const year = parseInt(dateString.substring(0, 4), 10)
  const month = parseInt(dateString.substring(4, 6), 10) - 1
  const day = parseInt(dateString.substring(6, 8), 10)

  return new Date(year, month, day)
}

export const getSelectedDates = (
  flightIndex: number,
  flights: readonly Partial<FlightRecordType>[],
  watchFlightType: FLIGHT_TYPE,
  defaultFlightType: FLIGHT_TYPE
) => {
  const flight = flights[flightIndex]

  if (flightIndex > 0) {
    return parseDate(flight?.startDate)
  }

  const isRoundTripFlight =
    (watchFlightType || defaultFlightType) === ROUND_TRIP

  if (isRoundTripFlight) {
    return {
      from: parseDate(flight?.startDate),
      to: parseDate(flight?.endDate)
    }
  }

  return parseDate(flight?.startDate)
}

export const getMultiDestinationSelectedDate = (
  flightIndex: number,
  flights: readonly Partial<FlightRecordType>[]
) => {
  const flight = flights[flightIndex]
  return parseDate(flight?.startDate)
}

export const onCalendarChange = (
  dates: Date | Date[] | DateRange | undefined,
  setValue?: UseFormSetValue<FlightFormStateType>,
  flightIndex = 0
): void => {
  if (!setValue) {
    return
  }

  if (dates instanceof Date) {
    // Single date selection
    const formattedDate = format(dates, 'yyyyMMdd')
    setValue(`flights.${flightIndex}.startDate` as const, formattedDate, {
      shouldDirty: true
    })
  } else if (dates && 'from' in dates && 'to' in dates) {
    // Date range selection
    if (dates.from) {
      const startDate = format(dates.from, 'yyyyMMdd')
      setValue(`flights.${flightIndex}.startDate` as const, startDate, {
        shouldDirty: true
      })
    }
    if (dates.to) {
      const endDate = format(dates.to, 'yyyyMMdd')
      setValue(`flights.${flightIndex}.endDate` as const, endDate, {
        shouldDirty: true
      })
    }
  }
}

export const createRadioOptions = () => {
  return FLIGHT_TYPE_OPTIONS.map(option => ({
    value: option.value,
    labelNode: option.label,
    name: 'flightType'
  }))
}

export const addFlightOnClick = (
  setValue: UseFormSetValue<FlightFormStateType>,
  watchFlights: readonly Partial<FlightRecordType>[] | undefined
): void => {
  const existingFlights = watchFlights || []
  const lastFlight = existingFlights[existingFlights.length - 1]

  const newFlight = {
    startLocation: lastFlight?.endLocation || ({} as LOCATION_SEARCH_TYPE),
    endLocation: {} as LOCATION_SEARCH_TYPE,
    startDate: '',
    endDate: ''
  }

  const updatedFlights = [...existingFlights, newFlight]
  setValue('flights', updatedFlights)
}
